import {
  createFileRoute,
  getRouteApi,
  Link,
  useRouter,
} from "@tanstack/react-router";
import { useState } from "react";
import {
  getDashboardRegistrations,
  getPendingApprovals,
  approveRegistration,
  denyRegistration,
} from "@/server/registration";
import { type PendingApprovalItem } from "@/apiTypes/registration";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, ChevronLeft, ChevronRight } from "lucide-react";
import { toastManager } from "@/components/ui/toast";
import { getInitials } from "@/utils/getInitials";
import { getProfileName } from "@/utils/getProfileName";
import {
  AlertDialog,
  AlertDialogPopup,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Avatar } from "@/components/ui/avatar";

const dashboardRoute = getRouteApi("/admin/dashboard/event/$eventId");

export const Route = createFileRoute(
  "/admin/dashboard/event/$eventId/participants",
)({
  component: ParticipantsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Number(search.page) || 1,
  }),
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: async ({ params, deps }) => {
    const [registrations, approvals] = await Promise.all([
      getDashboardRegistrations({
        data: { eventId: params.eventId, page: deps.page },
      }),
      getPendingApprovals({ data: params.eventId }),
    ]);
    return { registrations, approvals };
  },
});

/**
 * A single pending-approval row showing the rider's identity,
 * prerequisite pills, claimed date, and approve/deny buttons.
 */
function PendingApprovalRow({
  item,
  submitting,
  onApprove,
  onDeny,
}: {
  item: PendingApprovalItem;
  submitting: boolean;
  onApprove: () => void;
  onDeny: () => void;
}) {
  const name = getProfileName(item.profile);
  const isDenied = item.status === "DENIED";

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar profile={item.profile} />
          <span className="font-medium text-gray-900">{name}</span>
        </div>
      </TableCell>
      <TableCell className="text-gray-600">{item.profile.email}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {item.prerequisites.map((prereq) => (
            <span
              key={prereq.id}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
            >
              {prereq.name}
            </span>
          ))}
        </div>
      </TableCell>
      <TableCell className="text-gray-500">
        {new Date(item.claimedAt).toLocaleDateString()}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={submitting} onClick={onApprove}>
            Approve
          </Button>
          {!isDenied && (
            <Button
              size="sm"
              variant="outline"
              disabled={submitting}
              onClick={onDeny}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              Deny
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

type PendingAction = {
  type: "approve" | "deny";
  item: PendingApprovalItem;
};

/**
 * Pending approvals section rendered above the participants table.
 * Shows both PENDING_PREREQUISITE_REVIEW and DENIED registrations
 * so the organizer can act on them immediately. Approve and deny
 * actions require confirmation via a modal before executing.
 */
function PendingApprovalsSection({
  items,
  eventId,
}: {
  items: PendingApprovalItem[];
  eventId: string;
}) {
  const router = useRouter();
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  function startSubmitting(id: string) {
    setSubmittingIds((prev) => new Set(prev).add(id));
  }

  function stopSubmitting(id: string) {
    setSubmittingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  async function confirmAction() {
    if (!pendingAction) return;
    const { type, item } = pendingAction;
    setPendingAction(null);
    startSubmitting(item.id);
    try {
      if (type === "approve") {
        await approveRegistration({
          data: { registrationId: item.id, eventId },
        });
      } else {
        await denyRegistration({
          data: { registrationId: item.id, eventId },
        });
      }
      await router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : "Action failed",
        type: "error",
      });
    } finally {
      stopSubmitting(item.id);
    }
  }

  const confirmName = pendingAction
    ? getProfileName(pendingAction.item.profile)
    : "";

  return (
    <>
      <div className="mb-6">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-amber-700">
            Pending Approval ({items.length})
          </h3>
          <p className="text-xs text-gray-500">
            These riders claimed off-platform prerequisites and need review.
          </p>
        </div>
        <Card className="border border-amber-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Participant</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Prerequisites</TableHead>
                <TableHead>Claimed</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <PendingApprovalRow
                  key={item.id}
                  item={item}
                  submitting={submittingIds.has(item.id)}
                  onApprove={() => setPendingAction({ type: "approve", item })}
                  onDeny={() => setPendingAction({ type: "deny", item })}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.type === "approve"
                ? "Approve registration"
                : "Deny registration"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.type === "approve"
                ? `Approve ${confirmName}'s registration? They will be marked as registered for this event.`
                : `Deny ${confirmName}'s registration? They will not be able to participate unless approved later.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)}>
              Cancel
            </Button>
            <Button
              variant={
                pendingAction?.type === "deny" ? "destructive" : "default"
              }
              onClick={confirmAction}
            >
              {pendingAction?.type === "approve" ? "Approve" : "Deny"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

/** Participants table with pagination and pending approvals section. */
function ParticipantsPage() {
  const { registrations, approvals } = Route.useLoaderData();
  const { event } = dashboardRoute.useLoaderData();
  const { page, totalPages, totalCount } = registrations;

  const pendingItems = [...approvals.pending, ...approvals.denied];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Participants</h2>
          <p className="text-sm text-gray-500">
            {totalCount} registered{" "}
            {totalCount === 1 ? "participant" : "participants"}
          </p>
        </div>
      </div>

      {pendingItems.length > 0 && (
        <PendingApprovalsSection items={pendingItems} eventId={event.id} />
      )}

      {totalCount === 0 ? (
        <Card className="border border-gray-200">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              No participants registered yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Participant</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Registered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.registrations.map((reg) => (
                <TableRow key={reg.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {reg.profile.avatarUrl ? (
                        <img
                          src={reg.profile.avatarUrl}
                          alt={getProfileName(reg.profile)}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                          {getInitials(getProfileName(reg.profile))}
                        </div>
                      )}
                      <span className="font-medium text-gray-900">
                        {getProfileName(reg.profile)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {reg.profile.email}
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {reg.category.name}
                  </TableCell>
                  <TableCell className="text-gray-600">
                    {reg.route?.name || "\u2014"}
                  </TableCell>
                  <TableCell className="text-gray-500">
                    {new Date(reg.createdAt).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  asChild={page > 1}
                >
                  {page > 1 ? (
                    <Link
                      to="/admin/dashboard/event/$eventId/participants"
                      params={{ eventId: event.id }}
                      search={{ page: page - 1 }}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Link>
                  ) : (
                    <>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  asChild={page < totalPages}
                >
                  {page < totalPages ? (
                    <Link
                      to="/admin/dashboard/event/$eventId/participants"
                      params={{ eventId: event.id }}
                      search={{ page: page + 1 }}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
